import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth error:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin_token");
    setToken(null);
    setUser(null);
  };

  // Admin opens a dealer's portal: keep the admin token aside so they can return without logging in again.
  // Full page load so route guards see the new role cleanly.
  const impersonate = (dealerToken) => {
    localStorage.setItem("admin_token", localStorage.getItem("token"));
    localStorage.setItem("token", dealerToken);
    window.location.assign("/dealer/dashboard");
  };

  const returnToAdmin = () => {
    const adminToken = localStorage.getItem("admin_token");
    localStorage.removeItem("admin_token");
    if (adminToken) localStorage.setItem("token", adminToken); else localStorage.removeItem("token");
    window.location.assign(adminToken ? "/admin" : "/login");
  };

  const isImpersonating = !!localStorage.getItem("admin_token") && user?.role === "dealer";

  const getAuthHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, impersonate, returnToAdmin, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
